import { MediaFile, ProcessingCommand } from '../types/media';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://backend-kiro.vercel.app/api';

export class AudioProcessor {
  private uploadedFilename: string | null = null;
  
  constructor() {
    // No need for AudioContext in this implementation
  }
  
  async processAudio(file: MediaFile, command: ProcessingCommand): Promise<MediaFile> {
    try {
      // For serverless demo, we'll use the file URL directly
      // In production, you'd upload to cloud storage first
      const fileUrl = file.url || URL.createObjectURL(file.file);
      
      let processedUrl: string;
      
      // Process based on command type
      switch (command.type) {
        case 'cut':
          processedUrl = await this.cutAudio(fileUrl, command.parameters);
          break;
        case 'pitch':
        case 'tempo':
        case 'noise_reduction':
        case 'equalizer':
        case 'reverb':
        case 'voice_conversion':
          processedUrl = await this.applyAudioEffect(fileUrl, command);
          break;
        default:
          // Default to normalize effect
          processedUrl = await this.applyAudioEffect(fileUrl, {
            ...command,
            type: 'normalize'
          });
      }
      
      // For demo purposes, return a simulated processed file
      // In production, you'd download from the processed URL
      const processedFilename = `processed_${file.name}`;
      
      return {
        ...file,
        url: processedUrl,
        name: processedFilename,
        processed: true
      };
    } catch (error) {
      console.error('Audio processing failed:', error);
      throw new Error('Failed to process audio');
    }
  }
  
  private async uploadFile(file: File): Promise<string> {
    // For demo purposes, we'll simulate file upload and return a mock URL
    // In production, you'd upload to cloud storage (S3, Supabase, etc.)
    const mockFileUrl = `https://demo-storage.com/audio/${file.name}`;
    
    // Validate file with Vercel API
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'audio');
    
    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        console.log('File validated successfully');
      }
    } catch (error) {
      console.warn('File validation failed, continuing with demo mode');
    }
    
    return mockFileUrl;
  }
  
  private async cutAudio(fileUrl: string, parameters: any): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/process_audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        operation: 'cut',
        file_url: fileUrl,
        parameters: {
          start_time: parameters.startTime || 0,
          end_time: parameters.endTime
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Audio cut failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.output_url;
  }
  
  private async applyAudioEffect(fileUrl: string, command: ProcessingCommand): Promise<string> {
    // Try using the AI command processor first
    if (command.command) {
      try {
        const response = await fetch(`${API_BASE_URL}/process_command`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            command: command.command,
            file_url: fileUrl,
            file_type: 'audio'
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          return result.output_url;
        }
      } catch (error) {
        console.warn('AI command processing failed, falling back to direct API');
      }
    }
    
    // Fallback to direct API calls
    let operation = 'effect';
    let parameters = command.parameters;
    
    // Map command types to operations
    switch (command.type) {
      case 'pitch':
        operation = 'pitch';
        parameters = { semitones: parameters.pitch || 2 };
        break;
      case 'tempo':
        operation = 'tempo';
        parameters = { factor: parameters.tempo || 1.2 };
        break;
      default:
        operation = 'effect';
        parameters = {
          effect_type: command.type,
          ...parameters
        };
    }
    
    const response = await fetch(`${API_BASE_URL}/process_audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        operation,
        file_url: fileUrl,
        parameters
      })
    });
    
    if (!response.ok) {
      throw new Error(`Audio effect failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.output_url;
  }
  
  private async downloadFile(filename: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/download/${filename}`);
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response.blob();
  }
}